// angular
import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, throwError, timer } from "rxjs";
import { map, retry, switchMap } from "rxjs/operators";

// local
import {
  Guid,
  ISelfServData,
  ISelfServMeta,
  SelfServData,
  SelfServDataWireSchema,
  SelfServMetaStatus,
  SelfServMetaWireSchema,
} from "../models";
import { validateWireAs } from "../classes";

// TODO can we make this a setting
const rootUrl = "/api/selfserv";

@Injectable({ providedIn: "root" })
export class SelfServDataService {
  private readonly http = inject(HttpClient);

  // Validate a SelfServMeta payload against its wire schema. The meta
  // endpoints now return the payload bare (envelope retired), so the response
  // body IS the entity; the schema is the full server shape, narrowed to the
  // hand interface the SPA consumes. See design/wiki/wire-schemas.md.
  private validateMeta(data: unknown): ISelfServMeta {
    return validateWireAs<ISelfServMeta>(SelfServMetaWireSchema, data);
  }

  getMetaListByTeamId(teamId: Guid): Observable<ISelfServMeta[]> {
    const url = `${rootUrl}/meta?teamId=${teamId}`;

    // An empty/204 body parses to null, so the raw read is honestly nullable.
    return this.http
      .get<unknown[] | null>(url)
      .pipe(
        map((items) => (items ?? []).map((item) => this.validateMeta(item))),
      );
  }

  getMeta(
    selfServDomain: string,
    treeId: string | null = null,
  ): Observable<ISelfServMeta> {
    let url = `${rootUrl}/meta/${selfServDomain}`;

    if (treeId) {
      url += "?treeId=" + treeId;
    }

    return this.http
      .get<unknown>(url)
      .pipe(map((res) => this.validateMeta(res)));
  }

  getDataFromMeta(meta: ISelfServMeta): Observable<SelfServData> {
    // 30 seconds in total
    const maxRetries = 10;
    const retryInterval = 3000;

    // check if the given meta data value is valid
    const checkMeta = (value: ISelfServMeta) => {
      switch (value.metaStatus) {
        case SelfServMetaStatus.Pending:
          throw new Error("retry");

        case SelfServMetaStatus.Ok:
          return value;

        default:
        case SelfServMetaStatus.Error:
          throw new Error("Unknown error");
      }
    };

    // first try the initial meta
    try {
      return this.getDataFromUrl(checkMeta(meta));
    } catch {
      console.log(`initial meta invalid. Will retry ${maxRetries} times`);
    }

    // Retry strategy which will execute if the error contains 'retry' message
    // and the number of attempts had not reached the maximum. `retryCount` is
    // 1-based (1 on the first error), so retrying `maxRetries` times means
    // stopping once it exceeds `maxRetries`.
    const retryDelay = (error: any, retryCount: number) => {
      if (retryCount > maxRetries) {
        const message = `Failed to download meta after ${maxRetries} attempts`;
        console.error(message);
        return throwError(() => message);
      }

      if (error.message === "retry") {
        console.log(`Meta retry ${retryCount - 1}`);
        return timer(retryInterval);
      }

      console.error(`Invalid meta`, error.message);
      return throwError(() => error);
    };

    // re-fetch meta with conditional retry
    return this.getMeta(meta.domain).pipe(
      map(checkMeta), // check validity
      retry({ delay: retryDelay }), // handle errors
      switchMap((finalMeta) => this.getDataFromUrl(finalMeta)), // finally get data
    );
  }

  private getDataFromUrl(meta: ISelfServMeta): Observable<SelfServData> {
    // The render data is served bare (a blob at meta.dataUrl), not enveloped,
    // so it is validated directly. See design/wiki/wire-schemas.md.
    return this.http.get<ISelfServData>(meta.dataUrl).pipe(
      map((res) => {
        const data = validateWireAs<ISelfServData>(SelfServDataWireSchema, res);
        return new SelfServData(meta, data);
      }),
    );
  }

  /**
   * Resolve meta and data ina single call
   * @param selfServDomain SelfServ domain e.g. epicforest
   * @param treeId Optional tree id
   */
  getData(
    selfServDomain: string,
    treeId: string | null = null,
  ): Observable<SelfServData> {
    return this.getMeta(selfServDomain, treeId).pipe(
      switchMap((meta) => this.getDataFromMeta(meta)),
    );
  }
}
